import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, BarChart2, TrendingUp, Lightbulb, ClipboardList, Users, LineChart } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { DjayAvatar, DjayBubble } from '../../../mascot';
import type { AccountRole } from '../../types';
import { OnboardingProgress } from './OnboardingProgress';
import { getRoleThemeClasses } from '../../logic/uiHelpers';
interface OnboardingWelcomeStepProps {
  role: AccountRole;
  name: string;
  groupLevel?: number;
  totalSteps?: number;
  onNext: () => void;
  onGroupChange?: (group: number) => void;
}
export function OnboardingWelcomeStep({
  role,
  name,
  groupLevel = 6,
  totalSteps = 2,
  onNext,
  onGroupChange
}: OnboardingWelcomeStepProps) {
  const theme = getRoleThemeClasses(role);
  const getRoleConfig = () => {
    switch (role) {
      case 'learner':
        return {
          emotion: 'excited' as const,
          message: `Welkom ${name}! Ik ben Djay, jouw leermaatje. Samen gaan we oefenen en leren!`
        };
      case 'parent':
        return {
          emotion: 'happy' as const,
          message: 'Welkom! Hier kunt u de voortgang van uw kind volgen.'
        };
      case 'teacher':
        return {
          emotion: 'celebrating' as const,
          message: 'Welkom! Laten we uw klas klaarzetten.'
        };
      default:
        return {
          emotion: 'happy' as const,
          message: 'Welkom!'
        };
    }
  };
  const config = getRoleConfig();
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full max-w-md mx-auto" data-id="element-176">
      {/* Mascot Section */}
      <div className="flex flex-col items-center mb-6 relative" data-id="element-177">
        <motion.div animate={{
        y: [0, -8, 0]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-178">
          <DjayAvatar emotion={config.emotion} size="lg" data-id="element-179" />
        </motion.div>
        <div className="mt-3 w-full max-w-[260px]" data-id="element-180">
          <DjayBubble message={config.message} position="top" colorScheme={theme.colorScheme} autoDismissMs={0} data-id="element-181" />
        </div>
      </div>

      {/* Card */}
      <div className={`bg-white rounded-3xl p-6 sm:p-8 shadow-lg border ${theme.border} relative overflow-hidden`} data-id="element-182">
        <div className={`absolute top-0 left-0 w-full h-2 bg-gradient-to-r ${theme.gradient}`} data-id="element-183" />

        <OnboardingProgress currentStep={0} totalSteps={totalSteps} role={role} data-id="element-184" />

        {role === 'learner' && <div className="space-y-6" data-id="element-185">
            <div className="text-center" data-id="element-186">
              <h2 className="text-2xl font-black text-gray-900 mb-2" data-id="element-187">
                Klaar voor de start? 🚀
              </h2>
              <p className="text-gray-600 font-medium" data-id="element-188">
                Je gaat oefenen op het niveau van{' '}
                <span className="font-bold text-orange-600" data-id="element-189">
                  groep {groupLevel}
                </span>
                .
              </p>
            </div>

            <div className="bg-orange-50 border border-orange-100 rounded-2xl p-5 space-y-4" data-id="element-190">
              <motion.div className="flex items-center gap-3" initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.1
          }} data-id="element-191">
                <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-orange-500 flex-shrink-0" data-id="element-192">
                  <span className="text-xl" data-id="element-193">🎯</span>
                </div>
                <p className="text-sm font-medium text-orange-900" data-id="element-194">
                  Oefen rekenen, taal en meer op jouw niveau
                </p>
              </motion.div>
              <motion.div className="flex items-center gap-3" initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-195">
                <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-orange-500 flex-shrink-0" data-id="element-196">
                  <span className="text-xl" data-id="element-197">⭐</span>
                </div>
                <p className="text-sm font-medium text-orange-900" data-id="element-198">
                  Verdien XP en ontgrendel nieuwe titels
                </p>
              </motion.div>
              <motion.div className="flex items-center gap-3" initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.3
          }} data-id="element-199">
                <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-orange-500 flex-shrink-0" data-id="element-200">
                  <span className="text-xl" data-id="element-201">🔥</span>
                </div>
                <p className="text-sm font-medium text-orange-900" data-id="element-202">
                  Bouw een streak op door elke dag te oefenen
                </p>
              </motion.div>
            </div>
          </div>}

        {role === 'parent' && <div className="space-y-6" data-id="element-203">
            <h2 className="text-xl font-bold text-center text-gray-900" data-id="element-204">
              Wat kunt u hier doen?
            </h2>
            <ul className="space-y-4" data-id="element-205">
              <li className="flex items-center gap-3 text-gray-700" data-id="element-206">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-207">
                  <BarChart2 className="w-5 h-5 text-emerald-600" data-id="element-208" />
                </div>
                <span data-id="element-209">Voortgang per vak volgen</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-210">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-211">
                  <TrendingUp className="w-5 h-5 text-emerald-600" data-id="element-212" />
                </div>
                <span data-id="element-213">Groei over tijd bekijken</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-214">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-215">
                  <Lightbulb className="w-5 h-5 text-emerald-600" data-id="element-216" />
                </div>
                <span data-id="element-217">Persoonlijke oefentips</span>
              </li>
            </ul>
          </div>}

        {role === 'teacher' && <div className="space-y-6" data-id="element-218">
            <h2 className="text-xl font-bold text-center text-gray-900" data-id="element-219">
              Wat kunt u hier doen?
            </h2>
            <ul className="space-y-4" data-id="element-220">
              <li className="flex items-center gap-3 text-gray-700" data-id="element-221">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-222">
                  <ClipboardList className="w-5 h-5 text-indigo-600" data-id="element-223" />
                </div>
                <span data-id="element-224">Dagelijks klas-overzicht</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-225">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-226">
                  <Users className="w-5 h-5 text-indigo-600" data-id="element-227" />
                </div>
                <span data-id="element-228">Leerlingen beheren</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-229">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-230">
                  <LineChart className="w-5 h-5 text-indigo-600" data-id="element-231" />
                </div>
                <span data-id="element-232">Groei en trends volgen</span>
              </li>
            </ul>
          </div>}

        <div className="mt-8" data-id="element-233">
          <Button onClick={onNext} className={`w-full py-6 text-lg text-white ${theme.buttonBg}`} data-id="element-234">
            {role === 'learner' ? 'Laten we beginnen!' : 'Verder'}
            <ArrowRight className="w-5 h-5 ml-2" data-id="element-235" />
          </Button>
        </div>
      </div>
    </motion.div>;
}